/**
 * MfaVerify — segundo factor TOTP post-password (F3-01 AC-02).
 *
 * Flow real D2:
 *   1. Login.tsx hace password verify → backend retorna `{requiresMfa:true, mfaChallenge}`.
 *   2. Login redirige a /mfa-verify con challenge en state (ruta dedicada opcional) O resuelve inline.
 *   3. Esta page standalone queda para cuando se navega directo con state challenge
 *      (ej. deep-link re-verification) — usa el mismo endpoint POST /api/auth/mfa/verify
 *      body `{mfaChallenge, code}`.
 *
 * Shape alineado con AuthProvider.verifyMfa (F1) + mfa-verify-flow.spec.ts.
 *
 * Route: `/mfa-verify` (pública, acepta state con challenge).
 */

import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { api, ApiError, setToken } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { defaultPathForRole } from '@/lib/auth'

interface LocationState {
  mfaChallenge?: string
  redirectTo?: string
}

interface MfaVerifyApiResponse {
  token: string
}

interface FormValues {
  otp: string
}

export default function MfaVerifyPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { refresh, user } = useAuth()
  const state = (location.state ?? null) as LocationState | null
  const challenge = state?.mfaChallenge
  const redirectTo = state?.redirectTo

  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({ defaultValues: { otp: '' } })

  const verifyMutation = useMutation({
    mutationFn: async (code: string) => {
      if (!challenge) throw new ApiError(400, { error: 'missing_challenge' })
      return api.post<MfaVerifyApiResponse>(
        '/api/auth/mfa/verify',
        { mfaChallenge: challenge, code },
        { anonymous: true },
      )
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError && err.status === 401
          ? 'Código inválido'
          : err instanceof ApiError && err.status === 429
            ? 'Cuenta bloqueada por 15 minutos. Intenta más tarde.'
            : 'No se pudo verificar el código'
      setServerError(msg)
      reset()
    },
    onSuccess: async (res) => {
      setToken(res.token)
      await refresh()
      // Target rol-based si no hay redirectTo.
      const target = redirectTo ?? (user ? defaultPathForRole(user.role) : '/app')
      navigate(target, { replace: true })
    },
  })

  // Si llegan sin challenge, redirigir a login.
  useEffect(() => {
    if (!challenge) {
      navigate('/login', { replace: true })
    }
  }, [challenge, navigate])

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setServerError(null)
    await handleSubmit(async (values) => {
      await verifyMutation.mutateAsync(values.otp.trim())
    })(e)
  }

  if (!challenge) {
    // Render vacío mientras redirige (evita flash).
    return null
  }

  return (
    <main className="flex min-h-dvh flex-col bg-slate-50" data-testid="mfa-verify-container">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-4 py-10">
        <div className="mb-8 flex w-full flex-col items-start gap-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-fitlab-gradient px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white shadow-sm">
            <ShieldCheck className="h-3 w-3" aria-hidden />
            Verificación en dos pasos
          </span>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            <span className="bg-fitlab-gradient bg-clip-text text-transparent">FITLAB</span>
          </h1>
        </div>

        <div className="w-full rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-fitlab-blue)]/10">
                <ShieldCheck
                  className="h-5 w-5 text-[color:var(--color-fitlab-blue)]"
                  aria-hidden
                />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Ingresa tu código MFA</h2>
                <p className="text-sm text-slate-600">
                  Abre tu app de autenticación e ingresa el código de 6 dígitos.
                </p>
              </div>
            </div>

            <Input
              label="Código de verificación MFA (6 dígitos)"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              placeholder="123456"
              error={errors.otp?.message}
              data-testid="mfa-verify-input"
              {...register('otp', {
                required: 'Ingresa el código de 6 dígitos',
                pattern: {
                  value: /^\d{6}$/,
                  message: 'El código debe tener 6 dígitos',
                },
                onChange: (e) => {
                  e.target.value = e.target.value.replace(/\D/g, '').slice(0, 6)
                },
              })}
            />

            {serverError && (
              <div
                role="alert"
                className="rounded-md border border-[color:var(--color-fitlab-red)]/30 bg-[color:var(--color-fitlab-red)]/5 px-3 py-2 text-sm text-[color:var(--color-fitlab-red-hover)]"
                data-testid="mfa-verify-error"
              >
                {serverError}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/login', { replace: true })}
              >
                Volver
              </Button>
              <Button
                type="submit"
                loading={verifyMutation.isPending}
                className="flex-1"
                size="lg"
                data-testid="mfa-verify-submit"
              >
                Verificar
              </Button>
            </div>
          </form>
        </div>
      </div>
    </main>
  )
}
